<template>
  <div class="accessible-video-wrapper" role="group" :aria-labelledby="title ? titleId : undefined">
    <h3 v-if="title" class="font-medium mb-2" :id="titleId">{{ title }}</h3>

    <!-- Placeholder / Facade -->
    <button v-if="!active" class="video-facade h-full b-2" 
        @click="$emit('activate')" 
        @keydown.enter="$emit('activate')" 
        :aria-label="$t ? undefined : 'Reproducir ' + title"
        >
      <div class="facade-poster h-full" :style="facadePoster ? { backgroundImage: 'url(' + facadePoster + ')' } : {}">
        <svg class="facade-play-icon" viewBox="0 0 68 48" aria-hidden="true">
          <path class="facade-bg" d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.64 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill-opacity="0.8"/>
          <path d="M45 24L27 14v20" fill="#fff"/>
        </svg>
      </div>
    </button>

    <!-- Player activo -->
    <div v-else ref="playerMount" :key="'player-' + playerId" @keydown.esc.stop @keyup.esc.stop>
      <!-- YouTube via AblePlayer -->
      <video
        v-if="youtubeId"
        ref="video"
        :id="playerId"
        data-able-player
        data-skin="2020"
        preload="metadata"
        data-include-transcript="false"
        data-use-buttons-for-faster-slower="false"
        data-speech-rate="1"
        data-root-path="/libs/ableplayer/"
        :data-youtube-id="youtubeId"
        :data-youtube-desc-id="youtubeDescId || undefined"
        :data-youtube-quality="selectedQuality"
        :data-lang="lang"
        :data-heading-level="headingLevel"
        :data-meta-lang="lang"
        playsinline
      >
      </video>

      <!-- Video local via AblePlayer -->
      <video
        v-else
        ref="video"
        :id="playerId"
        data-able-player
        data-skin="2020"
        preload="metadata"
        data-include-transcript="false"
        data-use-buttons-for-faster-slower="false"
        data-speech-rate="1"
        data-root-path="/libs/ableplayer/"
        :data-lang="lang"
        :data-heading-level="headingLevel"
        :data-meta-lang="lang"
        :poster="poster || undefined"
        playsinline
      >
        <source :src="src" type="video/mp4" />
        <track
          v-if="captionsSrc"
          kind="captions"
          :src="captionsSrc"
          :srclang="lang"
          :label="captionsLabel"
          default
        />
        <track
          v-if="descriptionsSrc"
          kind="descriptions"
          :src="descriptionsSrc"
          :srclang="lang"
          :label="descriptionsLabel"
        />
      </video>
    </div>
  </div>
</template>

<script>
let instanceCounter = 0

export default {
  name: 'AccessibleVideo',
  props: {
    src: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: ''
    },
    captionsSrc: {
      type: String,
      default: null
    },
    descriptionsSrc: {
      type: String,
      default: null
    },
    lang: {
      type: String,
      default: 'es'
    },
    captionsLabel: {
      type: String,
      default: 'Español'
    },
    descriptionsLabel: {
      type: String,
      default: 'Audiodescripción'
    },
    poster: {
      type: String,
      default: null
    },
    headingLevel: {
      type: [String, Number],
      default: '0'
    },
    id: {
      type: [String, Number],
      default: null
    },
    active: {
      type: Boolean,
      default: false
    },
    youtubeId: {
      type: String,
      default: null
    },
    youtubeDescId: {
      type: String,
      default: null
    },
    youtubeQuality: {
      type: String,
      default: 'hd1080',
      validator: v => ['small', 'medium', 'large', 'hd720', 'hd1080', 'highres', 'default'].includes(v)
    }
  },
  data () {
    return {
      ablePlayerInstance: null,
      playerId: this.id ? 'able-' + this.id : 'able-player-' + (++instanceCounter),
      selectedQuality: this.youtubeQuality || 'hd1080'
    }
  },
  computed: {
    titleId () {
      return 'video-title-' + this.playerId
    },
    facadePoster () {
      if (this.poster) return this.poster
      // Deshabilitado por defecto
      // if (this.youtubeId) {
      //   return 'https://img.youtube.com/vi/' + this.youtubeId + '/hqdefault.jpg'
      // }
      return null
    }
  },
  watch: {
    active (val) {
      if (val) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            setTimeout(() => this.initPlayer(), 100)
          })
        })
      } else {
        this.destroyPlayer()
      }
    }
  },
  mounted () {
    if (this.active) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          setTimeout(() => this.initPlayer(), 100)
        })
      })
    }
    this._onClickOutside = (e) => {
      const $ = window.jQuery
      if (!$ || !this.$refs.playerMount) return
      const $mount = $(this.$refs.playerMount)
      const $visiblePopups = $mount.find('.able-volume-slider:visible, .able-popup:visible')
      if ($visiblePopups.length && !$visiblePopups[0].contains(e.target)) {
        if (this.ablePlayerInstance && typeof this.ablePlayerInstance.closePopups === 'function') {
          this.ablePlayerInstance.closePopups()
        }
      }
    }
    document.addEventListener('click', this._onClickOutside, true)

    // Interceptar ESC en fase de captura para que, si hay un popup de AblePlayer abierto
    // (ej: selector de volumen), se cierre el popup sin cerrar el diálogo padre.
    this._onEscCapture = (e) => {
      if (e.key !== 'Escape') return
      const $ = window.jQuery
      if (!$ || !this.ablePlayerInstance || !this.$refs.playerMount) return
      const mount = this.$refs.playerMount
      const $mount = $(mount)
      const $visiblePopups = $mount.find('.able-volume-slider:visible, .able-popup:visible')
      if ($visiblePopups.length) {
        e.stopPropagation()
        if (e.type === 'keyup' && typeof this.ablePlayerInstance.closePopups === 'function') {
          this.ablePlayerInstance.closePopups()
        }
      }
    }
    document.addEventListener('keydown', this._onEscCapture, true)
    document.addEventListener('keyup', this._onEscCapture, true)
  },
  beforeDestroy () {
    if (this._onClickOutside) {
      document.removeEventListener('click', this._onClickOutside, true)
    }
    if (this._onEscCapture) {
      document.removeEventListener('keydown', this._onEscCapture, true)
      document.removeEventListener('keyup', this._onEscCapture, true)
    }
    this.destroyPlayer()
  },
  methods: {
    initPlayer () {
      const $ = window.jQuery
      if (!$ || typeof window.AblePlayer === 'undefined') {
        if (this.$refs.video) {
          this.$refs.video.setAttribute('controls', '')
          this.$refs.video.focus()
        }
        return
      }
      try {
        const $video = $(this.$refs.video)
        this.ablePlayerInstance = new window.AblePlayer($video)
        this.moveFocusToPlayer()
      } catch (e) {
        console.error('[AccessibleVideo] Error al inicializar AblePlayer:', e)
        if (this.$refs.video) {
          this.$refs.video.setAttribute('controls', '')
        }
      }
    },
    moveFocusToPlayer () {
      if (this._focusObserver) this._focusObserver.disconnect()

      // El constructor de AblePlayer es asíncrono (carga traducciones antes de crear controles),
      // así que $playpauseButton no existe al instanciar. Observamos el DOM hasta que aparezca.
      this._focusObserver = new MutationObserver(() => {
        const btn = this.ablePlayerInstance && this.ablePlayerInstance.$playpauseButton
        if (btn && btn.length) {
          this._focusObserver.disconnect()
          this._focusObserver = null
          btn[0].focus()
        }
      })
      this._focusObserver.observe(this.$refs.playerMount, { childList: true, subtree: true })
    },
    destroyPlayer () {
      if (this._focusObserver) {
        this._focusObserver.disconnect()
        this._focusObserver = null
      }
      if (this.$refs.video) {
        try {
          this.$refs.video.pause()
        } catch (_) { /* el vídeo puede estar ya eliminado */ }
      }
      if (this.ablePlayerInstance) {
        // Limpiar el DOM generado por Able Player
        const $ = window.jQuery
        if ($ && this.$refs.playerMount) {
          $(this.$refs.playerMount).find('.able-wrapper').remove()
        }
      }
      this.ablePlayerInstance = null
    }
  }
}
</script>

<style scoped>
.accessible-video-wrapper {
  width: 100%;
}

.video-facade {
  cursor: pointer;
  position: relative;
  width: 100%;
  border-radius: 0.25rem;
  overflow: hidden;
  outline: none;
}

.video-facade:focus-visible {
  outline: 3px solid var(--primary-DEFAULT);
  outline-offset: 4px;
  border-radius: 0.25rem;
}

/* Fallback para navegadores sin soporte de :focus-visible */
.video-facade:focus:not(:focus-visible) {
  outline: none;
}

.facade-poster {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  background-color: #1a1a2e;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.facade-play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  height: 48px;
  transition: opacity 0.2s;
}

.facade-bg {
  fill: var(--primary-DEFAULT);
}

.video-facade:hover .facade-play-icon path:first-child,
.video-facade:focus .facade-play-icon path:first-child {
  fill: var(--primary-DEFAULT);
  fill-opacity: 1;
}
</style>
